//@flow
import React, {useMemo, useState, useRef, useCallback} from 'react';

import {makeStyles} from '@material-ui/core/styles';
import {
  Card,
  CardActionArea,
  Typography,
  FormControlLabel,
} from '@material-ui/core';
import {Edit, GetApp, AddCircleTwoTone} from '@material-ui/icons';
import {Link} from 'react-router-dom';
import {toPng} from 'html-to-image';
import IosSwitch from '../../../../../elements/IosSwitch/IosSwitch';
import {colors} from '../../../../../assets/styles/colors';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import backgroundCoupon from '../../../../../assets/img/backgroundCoupon.png';
import {commaFormat} from '../../../../../utils/number';
import AlertFeaturedModal from '../AlertFeaturedModal/AlertFeaturedModal';
import {
  getCustomDomain,
  useIsFeatureInTier,
} from '../../../../../utils/component';

import type {Coupon, Store, Currency} from '../../../../../entities';
import type {CouponRequest} from '../../api';

type Props = {|
  coupon?: Coupon,
  type?: 'CREATE',
  handleClick?: () => void,
  currency?: Currency,
  cleanCoupon?: () => void,
  customColor?: string,
  store?: Store,
  updateCouponSuccess?: boolean,
  updateCoupon?: ({couponRequest: CouponRequest, couponId: string}) => void,
  hasFeaturedCoupon?: boolean,
|};

const useStyles = props =>
  makeStyles(theme => ({
    root: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      position: 'relative',
      height: '100%',
      width: '100%',
      margin: '0 auto',
      padding: '16px',
      background: '#FCFCFD',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16)',
      borderRadius: '8px',
    },
    cardRoot: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      position: 'relative',
      height: '100%',
      width: '100%',
      boxShadow: 'unset',
      margin: '0 auto',
    },
    cardActionArea: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      alignItems: 'flex-start',
      height: '100%',
      borderRadius: '12px',
    },
    empty: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 216,
      height: '100%',
      width: '100%',
      border: '2px dashed #B8B8C7',
      opacity: 0.6,
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      padding: 20,
      borderRadius: '12px',
      '&:hover': {
        opacity: 1,
      },
      transition: 'opacity 0.2s',
    },
    titleWrap: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    title: {
      fontWeight: 600,
      fontSize: '14px',
      lineHeight: '22px',
      color: colors.grey800,
      margin: '0',
    },
    paragraph: {
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '22px',
      color: colors.grey700,
      margin: '0',
      maxWidth: '130px',
      textAlign: 'center',
    },
    chip: {
      background: '#00B382',
      borderRadius: '12px',
      color: 'white',
      padding: '2px 8px',
    },
    chipExpired: {
      background: '#E04763',
    },
    description: {
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0.005em',
      color: colors.grey800,
      marginBottom: '8px',
    },
    icon: {
      background: '#EEE5FF',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      marginRight: '4px',
      padding: '4px',
    },
    iconDownload: {
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      marginRight: '4px',
      padding: '4px',
    },
    buttonWrap: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: '-10px',
    },
    downloadCoupon: {
      borderTop: '1px solid #E8E8ED',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    switchContainer: {
      zoom: 0.8,
    },
    couponImage: {
      position: 'absolute',
      zIndex: '-1',
      opacity: 0,
      left: 0,
      top: 0,
      display: 'flex',
      width: '800px',
      background: '#FFFFFF',
      boxShadow:
        '0px 0px 1px rgba(40, 41, 61, 0.08), 0px 1px 3px rgba(96, 97, 112, 0.16)',
      borderRadius: '16px',
      alignItems: 'stretch',
    },
    couponRightSide: {
      width: '450px',
      padding: '24px',
    },
    couponLeftSide: {
      width: '330px',
      borderRadius: '8px 0px 0px 8px',
      backgroundColor: `${props.customColor}`,
      backgroundImage: `url(${backgroundCoupon})`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    contentCoupon: {
      textAlign: 'start',
    },
    couponLabel: {
      border: `1px dashed ${props.customColor}`,
      borderRadius: '5px',
      textAlign: 'center',
      padding: '6px 30px',
      textTransform: 'uppercase',
      marginBottom: '16px',
      fontSize: '20px',
      lineHeight: '24px',
    },
    couponTitle: {
      textAlign: 'center',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '24px',
      color: '#55556D',
      paddingBottom: '16px',
    },
    couponText: {
      overflowWrap: 'break-word',
      fontWeight: 400,
      fontSize: '18px',
      lineHeight: '24px',
      margin: '8px auto',
      color: '#55556D',
    },
    storeName: {
      color: `${props.customColor}`,
      textAlign: 'center',
      overflowWrap: 'break-word',
      fontWeight: 600,
      fontSize: '16px',
      lineHeight: '24px',
      marginTop: '14px',
    },
    triangle: {
      width: 0,
      marginLeft: '-1px',
      height: '100%',
      margin: 'auto',
      borderTop: '24px solid transparent',
      borderLeft: `16px solid ${props.customColor}`,
      borderBottom: '24px solid transparent',
    },
    bigText: {
      textAlign: 'center',
      fontWeight: 700,
      fontSize: '20px',
      lineHeight: '20px',
      color: 'white',
    },
    amountText: {
      textAlign: 'center',
      fontWeight: 700,
      fontSize: '80px',
      lineHeight: '80px',
      color: 'white',
      paddingRight: '2px',
    },
    labelDscto: {
      overflowWrap: 'break-word',
      fontWeight: 400,
      fontSize: '18px',
      lineHeight: '24px',
      color: 'white',
    },
    mediumText: {
      textAlign: 'center',
      fontWeight: 700,
      fontSize: '36px',
      lineHeight: '40px',
      color: 'white',
      paddingRight: '8px',
      display: 'flex',
      margin: 'auto',
      alignItems: 'center',
    },
    titleModal: {
      fontStyle: 'normal',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '24px',
      color: '#313135',
      textAlign: 'center',
    },
    bodyModal: {
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      color: '#55556D',
      textAlign: 'center',
    },
  }));

export default function CouponCard(props: Props) {
  const {
    coupon,
    type = '',
    handleClick,
    currency,
    cleanCoupon,
    store,
    updateCoupon,
    customColor = '#7B39FF',
    hasFeaturedCoupon,
  } = props;

  const hasStoreCouponFeature = useIsFeatureInTier('STORE_COUPON');
  const classes = useStyles({customColor})();

  const expDate = useMemo(() => {
    if (coupon) {
      return new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(coupon.expirationDate);
    }
  }, [coupon]);

  const actDate = useMemo(() => {
    if (coupon) {
      return new Intl.DateTimeFormat('es-PE', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
      }).format(coupon.activationDate);
    }
  }, [coupon]);

  const remainingDays = useMemo(() => {
    if (!coupon || !coupon.expirationDate) return null;
    const expDateObj = new Date(coupon.expirationDate);
    if (isNaN(expDateObj.getTime())) return null;

    const now = new Date();
    const diffTime = expDateObj.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 3600 * 24));
  }, [coupon]);

  const [openModal, setOpenModal] = useState(false);

  const handleSwitchDisplay = () => {
    if (updateCoupon && coupon) {
      if (coupon.featured || !hasFeaturedCoupon) {
        updateCoupon({
          couponRequest: {featured: !coupon.featured},
          couponId: coupon.id,
        });
      } else if (hasFeaturedCoupon) {
        setOpenModal(true);
      }
    }
  };

  const refImage = useRef<HTMLDivElement | null>(null);

  const handleDownloadCoupon = useCallback(() => {
    if (refImage.current === null) {
      return;
    }

    toPng(refImage.current, {
      style: {
        zIndex: 0,
        opacity: 1,
      },
      cacheBust: true,
    })
      .then(dataUrl => {
        const link = document.createElement('a');
        link.download = `${coupon ? coupon.code : 'coupon'}.png`;
        link.href = dataUrl;
        link.click();
      })
      .catch(err => {
        console.log(err);
      });
  }, [coupon]);

  return type === 'CREATE' ? (
    <Card classes={{root: classes.cardRoot}}>
      {handleClick && (
        <CardActionArea
          className={classes.cardActionArea}
          onClick={() => handleClick()}
        >
          <div className={classes.empty}>
            <AddCircleTwoTone color="primary" fontSize="large" />
            <Typography className={classes.paragraph}>
              Crear cupón de descuento
            </Typography>
          </div>
        </CardActionArea>
      )}
    </Card>
  ) : coupon && currency ? (
    <>
      <Card classes={{root: classes.root}}>
        <div className={classes.titleWrap}>
          <Typography className={classes.title} paragraph>
            {coupon.code}
          </Typography>

          <span
            className={`${classes.chip} ${
              coupon.enabled ? '' : classes.chipExpired
            }`}
          >
            {coupon.enabled ? 'Activo' : 'Inactivo'}
          </span>
        </div>
        <div style={{height: '100%'}}>
          <ul style={{paddingLeft: '16px', marginBottom: 0}}>
            {coupon.discountPercentage && (
              <li className={classes.description}>
                <span>{`${Math.round(
                  Number(coupon.discountPercentage) * 100
                )}% de descuento ${
                  coupon.couponConstraints.couponScopeSettings
                    ? coupon.couponConstraints.couponScopeSettings.scope ===
                      'ORDER'
                      ? 'en toda la orden'
                      : coupon.couponConstraints.couponScopeSettings.scope ===
                        'PRODUCT'
                      ? 'en productos seleccionados'
                      : 'en categorias seleccionadas'
                    : ''
                }`}</span>
              </li>
            )}
            {coupon.discountFixed && (
              <li className={classes.description}>
                <span>{`${commaFormat(
                  coupon.discountFixed,
                  currency.symbol
                )} de descuento en toda la orden`}</span>
              </li>
            )}

            {coupon.discountLimit && (
              <li className={classes.description}>
                <span>{`Límite de ${coupon.discountLimit} cupones, un cupón por persona`}</span>
              </li>
            )}
            {coupon.couponConstraints.orderItemsAmount && (
              <li className={classes.description}>
                <span>{`Descuento aplicado por compras mayor a ${commaFormat(
                  coupon.couponConstraints.orderItemsAmount,
                  currency.symbol
                )}`}</span>
              </li>
            )}
            {coupon.couponConstraints.orderItemsQuantity && (
              <li className={classes.description}>
                <span>{`Descuento aplicado por la compra de más de ${coupon.couponConstraints.orderItemsQuantity} productos`}</span>
              </li>
            )}

            {remainingDays !== null && (
              <li className={classes.description}>
                <span
                  style={{
                    fontWeight: 'bold',
                    color: remainingDays > 0 ? '#00B382' : '#E04763',
                  }}
                >
                  {remainingDays > 0
                    ? `Faltan ${remainingDays} días`
                    : 'Expirado'}
                </span>
              </li>
            )}
            {(actDate || expDate) && (
              <li className={classes.description}>
                <span>{`Del ${actDate || '?'} al ${expDate || '?'}`}</span>
              </li>
            )}

            <li className={classes.description}>
              {typeof coupon.couponConstraints?.usageLimit === 'number' ? (
                <span>
                  <b>{`Usados: ${coupon.usageCount} | Restantes: ${coupon
                    .couponConstraints.usageLimit - coupon.usageCount} de ${
                    coupon.couponConstraints.usageLimit
                  }`}</b>
                </span>
              ) : (
                <span>
                  <b>{`Usos totales: ${coupon.usageCount}`}</b>
                </span>
              )}
            </li>
          </ul>
        </div>
        <div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <FormControlLabel
                control={
                  <IosSwitch
                    className={classes.switchContainer}
                    name="enableOutside"
                    checked={coupon.featured}
                    onChange={handleSwitchDisplay}
                    disabled={!coupon.enabled || !hasStoreCouponFeature}
                  />
                }
                label={
                  <div className={classes.description} style={{margin: 0}}>
                    Mostrar en la tienda
                  </div>
                }
              />
            </div>

            <div className={classes.subtitleText}>
              <CustomButton
                component={Link}
                onClick={() => cleanCoupon && cleanCoupon()}
                to={`/marketing/coupons/${coupon.id}`}
              >
                {hasStoreCouponFeature ? (
                  <>
                    <div className={classes.icon}>
                      <Edit />
                    </div>
                    Editar
                  </>
                ) : (
                  'Ver'
                )}
              </CustomButton>
            </div>
          </div>
        </div>
        <div className={classes.downloadCoupon}>
          <CustomButton onClick={handleDownloadCoupon} style={{marginTop: 12}}>
            <div className={classes.iconDownload}>
              <GetApp />
            </div>
            Descargar Cupón
          </CustomButton>
        </div>
      </Card>
      <div className={classes.couponImage} ref={refImage}>
        <div className={classes.couponLeftSide}>
          {coupon.discountPercentage && (
            <div style={{display: 'flex', alignItems: 'center'}}>
              <div className={classes.amountText}>
                {Math.round(Number(coupon.discountPercentage) * 100)}
              </div>
              <div>
                <div className={classes.mediumText}>%</div>
                <div className={classes.bigText}>DSCT</div>
              </div>
            </div>
          )}
          {coupon.discountFixed && (
            <div style={{textAlign: 'center'}}>
              <div className={classes.amountText}>
                {Number(coupon.discountFixed)}
              </div>
              <div className={classes.mediumText}>SOLES</div>
            </div>
          )}
          {coupon.discountPercentage && (
            <div className={classes.labelDscto}>
              {coupon.couponConstraints.couponScopeSettings && (
                <div className={classes.labelDscto}>
                  {`${
                    coupon.couponConstraints.couponScopeSettings.scope ===
                    'ORDER'
                      ? 'de descuento en toda la orden'
                      : coupon.couponConstraints.couponScopeSettings.scope ===
                        'PRODUCT'
                      ? 'sobre productos seleccionados'
                      : 'sobre categorías seleccionadas'
                  }.`}
                </div>
              )}
            </div>
          )}
        </div>
        <div className={classes.triangle}></div>
        <div className={classes.couponRightSide}>
          <div className={classes.couponTitle}>Usa el código:</div>
          <div className={classes.couponLabel}>{coupon.code}</div>
          <div className={classes.contentCoupon}>
            <div className={classes.couponText}>
              {coupon.couponConstraints.dontApplyOnOfferPrices
                ? 'No aplica sobre precios en oferta.'
                : 'Aplica sobre precios en oferta.'}
            </div>
            {coupon.couponConstraints.couponScopeSettings && (
              <div className={classes.couponText}>
                {coupon.couponConstraints.couponScopeSettings.scope === 'ORDER'
                  ? 'Aplica sobre toda la orden'
                  : coupon.couponConstraints.couponScopeSettings.scope ===
                    'PRODUCT'
                  ? 'Aplica sobre productos seleccionados.'
                  : 'Aplica sobre categorías seleccionados.'}
              </div>
            )}
            {remainingDays !== null && (
              <div
                className={classes.couponText}
                style={{
                  fontWeight: 'bold',
                  color: remainingDays > 0 ? '#00B382' : '#E04763',
                }}
              >
                {remainingDays > 0
                  ? `Faltan ${remainingDays} días`
                  : 'Expirado'}
              </div>
            )}
            <div className={classes.couponText}>{`Del ${actDate ||
              '?'} al ${expDate || '?'}`}</div>
            {store && (
              <div className={classes.storeName}>{`www.${getCustomDomain(
                store
              ).replace('www.', '')}`}</div>
            )}
          </div>
        </div>
      </div>
      <AlertFeaturedModal
        openModal={openModal}
        handleFeaturedCoupon={() => {
          if (updateCoupon && coupon) {
            updateCoupon({
              couponRequest: {featured: !coupon.featured},
              couponId: coupon.id,
            });
            setOpenModal(false);
          }
        }}
        handleCloseModal={() => setOpenModal(false)}
      />
    </>
  ) : null;
}
